import { useEffect } from 'react';
import { IoTrashOutline } from 'react-icons/io5';
import { RiCloseCircleLine } from 'react-icons/ri';

import Loader from '@/components/Loader';
import { GroupMessage } from '@/exceptions/group/group.enum';
import useDeleteGroupMessage from '@/hooks/groupMessage/useDeleteGroupMessage';
import { useGetAllMessagesByGroupId } from '@/hooks/groupMessage/useGetAllMessageByGroupId';
import { notificationService } from '@/services/notification.service';

const GroupMessagesModal = ({
	closeModal,
	uuid,
}: {
	closeModal: () => void;
	uuid: string;
}) => {
	const { groupWithMessages, isLoading, isError } =
		useGetAllMessagesByGroupId(uuid);
	const { mutate: deleteGroupMessage } = useDeleteGroupMessage();

	const handleDelete = (messageUuid: string) => {
		notificationService.confirm(GroupMessage.DELETE_GROUP_MESSAGE, () => {
			deleteGroupMessage(messageUuid);
		});
	};

	useEffect(() => {
		document.body.style.overflow = 'hidden';

		return () => {
			document.body.style.overflow = 'auto';
		};
	}, []);

	return (
		<div data-test="create-category-modal" className="w-[100%] max-h-[90vh]">
			<div className="flex justify-end pb-2">
				<button data-test="create-category-modal-close" onClick={closeModal}>
					<RiCloseCircleLine size={20} />
				</button>
			</div>
			<h1 className="text-2xl text-center font-bold mt-2 mb-2">
				Group Messages
			</h1>
			<div className="max-h-[70vh] overflow-y-scroll">
				{isLoading ? (
					<div className="py-20">
						<Loader />
					</div>
				) : isError ? (
					<p className="text-center text-gray-500 text-2xl py-20">
						Something went wrong loading the messages, please try again later.
					</p>
				) : groupWithMessages?.messages.length === 0 ? (
					<p className="text-center text-gray-500 text-2xl py-20">
						No messages yet
					</p>
				) : (
					groupWithMessages?.messages?.map((message, index) => (
						<div
							key={index}
							className="flex flex-col justify-start gap-4 bg-slate-200 border rounded-md p-4 m-2"
							data-test="group-message"
						>
							<div className="flex justify-between">
								<h6 className="font-bold">
									{message.user.firstName + ' ' + message.user.lastName}
								</h6>
								<p>{new Date(message.createdAt).toLocaleString()}</p>
								<IoTrashOutline
									data-test="delete-message-button"
									className="cursor-pointer"
									onClick={() => handleDelete(message.uuid)}
								/>
							</div>
							<div className="flex gap-2">
								<p className="font-semibold">{'Message: ' + ' '}</p>
								<p>{message.groupMessage}</p>
							</div>
						</div>
					))
				)}
			</div>
		</div>
	);
};

export default GroupMessagesModal;
